import NProgress from 'nprogress';
import { createApp, defineAsyncComponent, ref, watch } from 'vue';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import { setGeneralDebugInfo } from './composables/useRemunerationDebugInfo';
import { lazyImport, clearChunkReloadFlag } from './utils/lazyImport';
import VueTelInput from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
// TODO: probably move this to seperate 'module' files or something
const DataTable = defineAsyncComponent(lazyImport(() => import('./components/DataTable.vue')));
const CompanySwitcher = defineAsyncComponent(lazyImport(() => import('./components/CompanySwitcher.vue')));
const SubscriptionComponent = defineAsyncComponent(lazyImport(() => import('./components/SubscriptionComponent.vue')));
const OrganizationCompanyForm = defineAsyncComponent(lazyImport(() => import('./components/OrganizationCompanyForm.vue')));
const FunctionsPageFormNew = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/FunctionsPageFormNew.vue')));
const FunctionsPageFormOld = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/FunctionsPageForm.vue')));
const OrganisationDataForm2 = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/OrganisationDataForm2.vue')));
const OrganizationRemunerationShareForm = defineAsyncComponent(lazyImport(() => import('./components/OrganizationRemunerationShareForm.vue')));
const WorkingHoursForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/WorkingHoursForm.vue')));
const FunctionGroupFormNew = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/FunctionGroupFormNew.vue')));
const FunctionGroupFormOld = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/FunctionGroupForm.vue')));
const SurchargesForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/SurchargesForm.vue')));
const WageIncrementsForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/WageIncrementsForm.vue')));
const WageIncrementForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/WageIncrementForm.vue')));
const UntaxedExpensesForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/UntaxedExpensesForm.vue')));
const HolidayAllowanceForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/HolidayAllowanceForm.vue')));
const InsurancesForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/InsurancesForm/InsurancesForm.vue')));
const EmploymentConditionsForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/OtherEmploymentConditionsForm/OtherEmploymentConditionsForm.vue')));
const UpdatePaymentInformationForm = defineAsyncComponent(lazyImport(() => import('./components/UpdatePaymentInformationForm.vue')));
const ExtendSubscriptionComponent = defineAsyncComponent(lazyImport(() => import('./components/ExtendSubscriptionComponent.vue')));
const OrganizationSubscriptionComponent = defineAsyncComponent(lazyImport(() => import('./components/OrganizationSubscriptionComponent.vue')));
const SubscriptionBundleInformation = defineAsyncComponent(lazyImport(() => import('./components/SubscriptionBundleInformation.vue')));
const TravelExpensesForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/TravelExpensesForm.vue')));
const AssistRequestSettingsComponent = defineAsyncComponent(lazyImport(() => import('./components/AssistRequestSettingsComponent.vue')));
const BackofficeForm = defineAsyncComponent(lazyImport(() => import('./components/BackofficeForm.vue')));
const PublishDatePopup = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/PublishDatePopup.vue')));
const HirerRemunerationStatusUpdatePage = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Publish/HirerRemunerationStatusUpdatePage.vue')));
const CAOConsultComponent = defineAsyncComponent(lazyImport(() => import('./components/CAOConsultComponent.vue')));
const Alert = defineAsyncComponent(lazyImport(() => import('./components/Inputs/Alerts/Alert.vue')));
// Subscriptions New components
const SubscriptionPageForm = defineAsyncComponent(lazyImport(() => import('./components/SubscriptionPageForm.vue')));
const CAOEmailPreferenceForm = defineAsyncComponent(lazyImport(() => import('./components/CAOEmailPreferenceForm.vue')));
const CAONewsComponent = defineAsyncComponent(lazyImport(() => import('./components/CAONewsComponent.vue')));
const CAOWageIncrements = defineAsyncComponent(lazyImport(() => import('./components/CAOWageIncrements.vue')));
const DataTableNew = defineAsyncComponent(lazyImport(() => import('./components/DataTableNew.vue')));
const PortalPopup = defineAsyncComponent(lazyImport(() => import('./components/PortalPopup.vue')));
const GlobalCLAPrefillComponent = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/GlobalPrefill/GlobalCLAPrefillComponent.vue')));
const GlobalPrefillFab = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/GlobalPrefill/GlobalPrefillFab.vue')));
const LeaveForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/10LeaveForm.vue')));
const IndividualChoiceBudgetForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/IndividualChoiceBudgetForm.vue')));
const SustainableEmployabilityForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/SustainableEmployability/SustainableEmployabilityForm.vue')));
const SpecialPaymentsForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/SpecialPaymentsForm.vue')));
const SickPayForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/SickPayForm.vue')));
const PensionForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/Pension/PensionForm.vue')));
const PaidBreakForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/PaidBreakForm.vue')));
const ReimbursementsData = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/Reimbursement/ReimbursementsData.vue')));
const TravelExpensesData = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/TravelExpense/TravelExpenseData.vue')));
const AllowanceForm = defineAsyncComponent(lazyImport(() => import('./components/Remuneration/Forms/AllowanceForm.vue')));
/**
 * General components
 */
// Context menu ILB overview
const ContextMenu = defineAsyncComponent(lazyImport(() => import('./components/General/ContextMenu.vue')));
// API components
const ContractSigningComponent = defineAsyncComponent(lazyImport(() => import('./components/Api/ContractSigningComponent.vue')));
const ApiTokensComponent = defineAsyncComponent(lazyImport(() => import('./components/Api/ApiTokensComponent.vue')));
const TextCopyField = defineAsyncComponent(lazyImport(() => import('./components/InputFields/TextCopyField.vue')));
// registration forms
const RegistrationHirer = defineAsyncComponent(lazyImport(() => import('./components/Authentication/RegistrationHirer.vue')));
const RegistrationLender = defineAsyncComponent(lazyImport(() => import('./components/Authentication/RegistrationLender.vue')));
/**
 * Initialize the Vue app and bind the web components
 */
const app = createApp({
    components: {
        'data-table': DataTable,
        'company-switcher': CompanySwitcher,
        'subscription-component': SubscriptionComponent,
        'organization-company-form': OrganizationCompanyForm,
        'functions-page-form-new': FunctionsPageFormNew,
        'functions-page-form-old': FunctionsPageFormOld,
        'organisation-data-form-2': OrganisationDataForm2,
        'organization-remuneration-share-form': OrganizationRemunerationShareForm,
        'working-hours-form': WorkingHoursForm,
        'function-group-form-new': FunctionGroupFormNew,
        'function-group-form-old': FunctionGroupFormOld,
        'surcharges-form': SurchargesForm,
        // 'wage-increments-form': WageIncrementsForm,
        'wage-increment-form': WageIncrementForm,
        'untaxed-expenses-form': UntaxedExpensesForm,
        'insurances-form': InsurancesForm,
        'employment-conditions-form': EmploymentConditionsForm,
        'holiday-allowance-form': HolidayAllowanceForm,
        'update-payment-information-form': UpdatePaymentInformationForm,
        'extend-subscription-component': ExtendSubscriptionComponent,
        'organization-subscription-component': OrganizationSubscriptionComponent,
        'subscription-bundle-information': SubscriptionBundleInformation,
        'travel-expenses-form': TravelExpensesForm,
        'assist-request-settings-component': AssistRequestSettingsComponent,
        'backoffice-form': BackofficeForm,
        'registration-hirer': RegistrationHirer,
        'registration-lender': RegistrationLender,
        'publish-date-popup': PublishDatePopup,
        'hirer-remuneration-status-update-page': HirerRemunerationStatusUpdatePage,
        'contract-signing-component': ContractSigningComponent,
        'api-tokens-component': ApiTokensComponent,
        'text-copy-field': TextCopyField,
        'cao-consult-component': CAOConsultComponent,
        'cao-email-preference-form': CAOEmailPreferenceForm,
        'cao-news-component': CAONewsComponent,
        'cao-wage-increments': CAOWageIncrements,
        'data-table-new': DataTableNew,
        'portal-popup': PortalPopup,
        'context-menu': ContextMenu,
        'leave-form': LeaveForm,
        'special-payments-form': SpecialPaymentsForm,
        'sustainable-work-and-life-form': SustainableEmployabilityForm,
        'individual-choice-budget-form': IndividualChoiceBudgetForm,
        'paid-break-form': PaidBreakForm,
        'sick-pay-form': SickPayForm,
        'pension-form': PensionForm,
        'allowance-form': AllowanceForm,
        'reimbursements-data': ReimbursementsData,
        'travel-expenses-data': TravelExpensesData,
        'alert': Alert,
        'subscription-page-form': SubscriptionPageForm,
        'global-cla-prefill-component': GlobalCLAPrefillComponent,
        'global-prefill-fab': GlobalPrefillFab,
    },
});
/**
 * Provide the global loadingState to child components
 */
const isLoading = ref(false);
const setLoadingState = (state) => (isLoading.value = state);
watch(isLoading, (n, o) => (n === true ? NProgress.start() : NProgress.done()));
app.provide('loadingState', {
    isLoading,
    setLoadingState,
});
/**
 * Provide the global canEdit state to child components
 */
const canEdit = ref(true);
const setEditState = (state) => (canEdit.value = state);
app.provide('canEdit', { canEdit, setEditState });
/**
 * Provide the global activePopups state to child components
 */
const activePopups = ref([]);
/**
 * Add a popup to the activePopups array
 * @param popup
 */
function addPopup(popup) {
    return activePopups.value.push(popup);
}
/**
 * Remove a popup from the activePopups array
 * @param popupUID
 */
function removePopup(popupUID) {
    if (!popupUID) {
        return;
    }
    return (activePopups.value = activePopups.value.filter((p) => p !== popupUID));
}
/**
 * Watch the activePopups array and close the last popup when the array is empty
 */
app.provide('activePopups', { activePopups, addPopup, removePopup });
/**
 * Provide the global canPrefill state to child components
 */
const canPrefill = ref(false);
function setPrefillState(state) {
    if (state === 0 || state === 1) {
        canPrefill.value = Boolean(state);
    }
    else if (typeof state === 'boolean') {
        canPrefill.value = state;
    }
}
app.provide('canPrefill', { canPrefill, setPrefillState });
/**
 * Provide global theme directory for icons
 */
app.provide('themeDir', '/_resources/themes/inlenersbeloning');
app.use(VueTelInput);
/**
 * Start & Install Bugsnag
 */
if (!process.env.MIX_DISABLE_BUGSNAG) {
    Bugsnag.start({
        apiKey: process.env.MIX_BUGSNAG_APIKEY ?? '',
        plugins: [new BugsnagPluginVue()],
        releaseStage: process.env.MIX_BUGSNAG_RELEASE_STAGE || 'development',
        appVersion: '1.0.0',
    });
    app.use(Bugsnag.getPlugin('vue'));
}
/**
 * Leave a CAO deviation debug trail whenever a remuneration step form loads: a breadcrumb with
 * the remuneration's ID/Hash, plus the data behind the general debug panel (see
 * useRemunerationDebugInfo.ts). Only the top-level step Forms declare `context` as an actual
 * prop (children read it via inject), so this fires once per step-form mount without needing
 * to touch every Forms/*.vue file.
 */
app.mixin({
    onBeforeMount() {
        const context = this.$props?.context;
        const remuneration = context?.Remuneration;
        if (remuneration?.ID == null)
            return;
        setGeneralDebugInfo(context);
        // Bugsnag is disabled in development and test environments, so skip the breadcrumb if it's not active
        if (process.env.MIX_DISABLE_BUGSNAG)
            return;
        Bugsnag.leaveBreadcrumb('Remuneration form loaded', {
            RemunerationID: remuneration.ID,
            RemunerationHash: remuneration.Hash,
        });
    },
});
/**
 * Mount the App to the dom.
 */
app.mount('#app');
/**
 * The app booted successfully, so clear the one-time chunk-reload guard. This
 * lets a *future* stale-bundle situation (after the next deploy) trigger its own
 * single recovery reload rather than being blocked by a leftover flag.
 */
clearChunkReloadFlag();
